import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AlertTriangle } from 'lucide-react';

/**
 * Every board in the store, in kilograms.
 *
 * Board is bought, costed and wasted by weight, so this is the shape the store
 * actually thinks in: opening, what came in, what the jobs took, what is left.
 * Consumption is fed by the imposition that priced each job, which is why the
 * consumed column can be trusted without anyone keying it in.
 *
 * Eight columns of that do not fit a phone, so below lg each board becomes a
 * card: closing weight large because it is the number anybody is looking for,
 * the three figures it is made of underneath it.
 */

const kg = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const HEADS = ['Board', 'Form', 'GSM', 'Opening', 'Purchased', 'Consumed', 'Closing', 'Location'];

const FILTERS = [['all', 'All board'], ['in_stock', 'In stock'], ['negative', 'Negative stock']];

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5 sm:p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-lg font-bold tabular-nums sm:text-xl ${tone || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

function Flags({ row }) {
  return (
    <>
      {row.negative && <Badge className="ml-1 bg-red-50 text-[9px] text-red-700">negative</Badge>}
      {row.below_minimum && <Badge className="ml-1 bg-amber-50 text-[9px] text-amber-700">low</Badge>}
    </>
  );
}

function PaperCard({ row }) {
  return (
    <li
      data-testid={`paper-row-${row.item_id}`}
      className={cn(
        'rounded-lg border bg-white p-3 shadow-card',
        row.negative ? 'border-l-4 border-slate-200 border-l-red-500'
          : row.below_minimum ? 'border-l-4 border-slate-200 border-l-amber-500'
            : 'border-slate-200'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {row.name}
            {row.paper?.reel_no && <span className="ml-1 text-[10px] text-slate-400">#{row.paper.reel_no}</span>}
          </p>
          <p className="mt-0.5 truncate text-xs capitalize text-slate-500">
            {row.paper?.form || '—'}
            {row.paper?.gsm ? ` · ${row.paper.gsm} gsm` : ''}
            {row.location ? ` · ${row.location}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className={cn(
            'text-xl font-bold leading-none tabular-nums',
            row.negative ? 'text-red-600' : row.below_minimum ? 'text-amber-600' : 'text-slate-900'
          )}>
            {kg(row.closing_kg)}
          </p>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">closing kg</p>
        </div>
      </div>

      <dl className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-center">
        {[['Opening', kg(row.opening_kg), 'text-slate-600'],
          ['Purchased', kg(row.purchased_kg), 'text-emerald-700'],
          ['Consumed', kg(row.consumed_kg), 'text-slate-600']].map(([label, value, tone]) => (
          <div key={label}>
            <dt className="text-[10px] uppercase tracking-wider text-slate-400">{label}</dt>
            <dd className={`text-sm font-medium tabular-nums ${tone}`}>{value}</dd>
          </div>
        ))}
      </dl>

      {(row.negative || row.below_minimum) && (
        <p className="mt-2 text-right"><Flags row={row} /></p>
      )}
    </li>
  );
}

export default function PaperLedger() {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all'); // all | negative | in_stock

  const load = useCallback(async () => {
    try {
      const res = await api.get('/inventory/paper/ledger', {
        params: {
          negative_only: filter === 'negative',
          in_stock_only: filter === 'in_stock',
        },
      });
      setData(res.data);
    } catch {
      toast.error('Could not load the paper ledger');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="py-10 text-center text-sm text-slate-400">Loading…</p>;

  const emptyMessage = 'No paper on the books yet — add a stock item with a paper spec.';

  return (
    <div className="space-y-3" data-testid="paper-ledger">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <Stat label="Opening (kg)" value={kg(data.totals.opening)} />
        <Stat label="Purchased (kg)" value={kg(data.totals.purchased)} />
        <Stat label="Consumed (kg)" value={kg(data.totals.consumed)} />
        <Stat label="Closing (kg)" value={kg(data.totals.closing)}
          tone={data.totals.closing < 0 ? 'text-red-600' : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* the filter is the whole navigation of this view, so on a phone the
            three choices share the row rather than shrinking to fit beside text */}
        <div className="grid w-full grid-cols-3 gap-1.5 sm:flex sm:w-auto">
          {FILTERS.map(([value, label]) => (
            <Button key={value} size="sm" variant={filter === value ? 'default' : 'outline'}
              className="h-10 px-2 text-[13px] sm:h-8 sm:px-3"
              onClick={() => setFilter(value)} data-testid={`paper-filter-${value}`}>
              {label}
            </Button>
          ))}
        </div>
        {data.negative_count > 0 && (
          <span className="flex items-start gap-1 text-xs text-red-600" data-testid="negative-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {data.negative_count} board{data.negative_count > 1 ? 's have' : ' has'} gone negative —
              more was issued than the store shows
            </span>
          </span>
        )}
      </div>

      {isMobile ? (
        data.rows.length === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white px-3 py-10 text-center text-sm text-slate-400">
            {emptyMessage}
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="paper-cards">
            {data.rows.map(row => <PaperCard key={row.item_id} row={row} />)}
          </ul>
        )
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid="paper-table">
            <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {HEADS.map(h => (
                  <th key={h} className="whitespace-nowrap px-2.5 py-2 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={8} className="px-2 py-10 text-center text-slate-400">{emptyMessage}</td></tr>
              ) : data.rows.map(row => (
                <tr key={row.item_id} className="border-t border-slate-200"
                  data-testid={`paper-row-${row.item_id}`}>
                  <td className="px-2.5 py-2">
                    {row.name}
                    {row.paper?.reel_no && (
                      <span className="ml-1 text-[10px] text-slate-400">#{row.paper.reel_no}</span>
                    )}
                  </td>
                  <td className="px-2.5 py-2 capitalize text-slate-500">{row.paper?.form || '—'}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-500">{row.paper?.gsm || '—'}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{kg(row.opening_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-emerald-700">{kg(row.purchased_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-slate-600">{kg(row.consumed_kg)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums font-medium">
                    <span className={row.negative ? 'text-red-600' : row.below_minimum ? 'text-amber-600' : ''}>
                      {kg(row.closing_kg)}
                    </span>
                    <Flags row={row} />
                  </td>
                  <td className="px-2.5 py-2 text-slate-500">{row.location || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
